import axios, { type AxiosError } from 'axios'
import { useAtomValue } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { GarageAvailabilityData, GarageListResponse, GaragePricesResponse } from '../types'
import type {
    Currency,
    Garage,
    GarageAvailability,
    HoverPoint,
    MinMaxRange,
} from '../../types'
import { searchSubmissionAtom } from '../../state'
import { useMemo } from 'react'
import { useApplySuggestedStartTime } from '../../hooks/use-apply-suggested-start-time'
import { deriveLaterAvailability } from '../later-availability'
import { useLaterAvailabilityQuery } from './use-later-availability'
import { normalizeWeightRange } from '../../weight-range'

type AvailabilityApiResponse = GarageAvailabilityData[] | { errors?: unknown }

const getAvailabilityItems = (data: AvailabilityApiResponse | undefined): GarageAvailabilityData[] => {
    if (Array.isArray(data)) {
        return data
    }

    return []
}

const getResponseErrors = (data: unknown): string[] => {
    if (!data || typeof data !== 'object' || !('errors' in data) || !Array.isArray(data.errors)) {
        return []
    }

    return data.errors.filter(error => typeof error === 'string' && error.trim().length > 0)
}

const getRequestErrors = (error: Error | null, fallback: string): string[] => {
    const errors = getResponseErrors((error as AxiosError | null)?.response?.data)
    return errors.length > 0 ? errors : [fallback]
}

const getRange = <T,>(
    items: T[] | null | undefined,
    selector: (item: T) => number | null | undefined,
    transform?: (value: number) => number
): MinMaxRange | null => {
    if (!items || items.length === 0) return null

    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY

    for (const item of items) {
        const raw = selector(item)
        if (typeof raw !== 'number' || Number.isNaN(raw)) continue

        const value = transform ? transform(raw) : raw

        min = Math.min(min, value)
        max = Math.max(max, value)
    }

    return min === Number.POSITIVE_INFINITY ? null : { min, max }
}

type UseGetGarageListResult = {
    searchLocationPoint: HoverPoint | null,
    garageList: Array<Garage>,
    loadingGarageList: boolean,
    loadingGarageAvailability: boolean,
    availabilityErrors: string[],
    priceRange: MinMaxRange | null,
    heightRange: MinMaxRange | null,
    widthRange: MinMaxRange | null,
    lengthRange: MinMaxRange | null,
    weightRange: MinMaxRange | null,
}

export const useGetGarageList = (
    userLang: string,
    currency: Currency,
): UseGetGarageListResult => {

    const { t } = useTranslation()
    const searchSubmission = useAtomValue(searchSubmissionAtom)
    const {
        location,
        startDate,
        startTime,
        endDate,
        endTime,
    } = searchSubmission

    const { data: searchData } = useQuery({
        queryKey: [
            'garages',
            location,
            userLang,
        ],
        queryFn: async () => {
            const { data } = await axios.get<GarageListResponse>(
                `/api/garages/list?location=${location ?? ''}&locale=${userLang}`,
            )
            return data
        },
        staleTime: 1000*60*15,
    })

    const searchLocationPoint = useMemo<HoverPoint | null>(() => {
        if (!searchData) return null

        const { latitude, longitude } = searchData.search_location
        if (
            typeof latitude !== 'number' ||
            !Number.isFinite(latitude) ||
            typeof longitude !== 'number' ||
            !Number.isFinite(longitude)
        ) {
            return null
        }

        return {
            id: 1,
            lat: latitude,
            lng: longitude,
        }
    }, [searchData])

    const garages = useMemo(() => {
        if (!searchData) return null
        return searchData.garages.map(garage => ({
            id: garage.id,
            name: garage.name,
            reservationSlug: garage.reservation_slug,
            amountCentsCzk: null,
            amountCentsHourlyCzk: null,
            amountCentsEur: null,
            amountCentsHourlyEur: null,
            height: garage.car_height_limit ? Number.parseFloat(garage.car_height_limit.replace(",", ".")) : null,
            length: garage.car_length_limit ? Number.parseFloat(garage.car_length_limit.replace(",", ".")) : null,
            weight: garage.car_weight_limit ? Number.parseFloat(garage.car_weight_limit.replace(",", ".")) : null,
            width: garage.car_width_limit ? Number.parseFloat(garage.car_width_limit.replace(",", ".")) : null,
            distance: garage.distance_to_query,
            image: garage.main_image_url,
            available: false,
            timeRangeValid: null as boolean | null,
            availabilityErrors: [] as string[],
            lat: Number.parseFloat(garage.latitude),
            lng: Number.parseFloat(garage.longitude),
            minimumStayHours: garage.minimum_stay_hours,
            reservationTimeRange: null,
            priceLoading: true,
            checkLaterAvailability: garage.is_later_available,
            showPopup: garage.show_popup,
            popupText: garage.popup_text,
            electricCarNotAllowed: garage.electric_car_not_allowed,
            lpgCngNotAllowed: garage.lpg_cng_not_allowed,
            motorbikesNotAllowed: garage.motorbikes_not_allowed,
            parkingLot: garage.parking_lot,
        })).sort((a, b) => a.distance - b.distance)
    }, [searchData])

    const availabilityQuery = useQuery<AvailabilityApiResponse>({
        queryKey: [
            'garagesAvailability',
            startDate?.format('YYYY-MM-DD'),
            startTime,
            endDate?.format('YYYY-MM-DD'),
            endTime,
        ],
        queryFn: async () => {
            const { data } = await axios.get<AvailabilityApiResponse>(
                `/api/garages/availability?begins_at_date=${
                    startDate?.format('YYYY-MM-DD') ?? ''}&begins_at_time=${
                    startTime ?? ''}&ends_at_date=${
                    endDate?.format('YYYY-MM-DD') ?? ''}&ends_at_time=${
                    endTime ?? ''}`,
            )
            return data
        },
        staleTime: 1000*60*10,
    })
    const availabilityResponse = availabilityQuery.isError ? undefined : availabilityQuery.data
    const availabilityData = getAvailabilityItems(availabilityResponse)
    const hasAvailabilityResponse = typeof availabilityResponse !== 'undefined'

    const garagesWithAvailability = useMemo(() => {
        if (!garages || !hasAvailabilityResponse) return null
        return garages.map(garage => {
            const currentGarageAvailability = availabilityData.find(item => item.id === garage.id)
            return {
                ...garage,
                available: currentGarageAvailability ? currentGarageAvailability.available : false,
                timeRangeValid: currentGarageAvailability?.time_range_valid ?? null,
                availabilityErrors: getResponseErrors(currentGarageAvailability),
                lastPlacesAvailable: !!currentGarageAvailability?.available && currentGarageAvailability?.last_places_available || false,
            }
        })
    }, [availabilityData, garages, hasAvailabilityResponse])

    // const garageIds = useMemo(
    //     () => Array.from(new Set((garages ?? []).map(garage => garage.id))),
    //     [garages],
    // )

    const priceQuery = useQuery<GaragePricesResponse | null>({
        queryKey: [
            'garagePricesBatch',
            //garageIds,
            startDate?.format('YYYY-MM-DD'),
            startTime,
            endDate?.format('YYYY-MM-DD'),
            endTime,
        ],
        queryFn: async () => {
            const { data } = await axios.get<GaragePricesResponse>(
                '/api/garages/prices_in_batch',
                {
                    params: {
                        // garage_ids: JSON.stringify(garageIds),
                        begins_at_date: startDate?.format('YYYY-MM-DD') ?? '',
                        begins_at_time: startTime ?? '',
                        ends_at_date: endDate?.format('YYYY-MM-DD') ?? '',
                        ends_at_time: endTime ?? '',
                    },
                },
            )

            return data
        },
        enabled: Boolean(garages && garages.length > 0),
        //enabled: garageIds.length > 0,
        staleTime: 1000*60*15,
    })
    const garagePricesBatch = priceQuery.isError ? undefined : priceQuery.data
    const priceLoading = priceQuery.isFetching && (priceQuery.isPending || priceQuery.isError)

    const pricesByGarageId = useMemo(() => {
        const prices = garagePricesBatch?.prices ?? []
        return new Map(prices.map(p => [p.garage_id, p]))
    }, [garagePricesBatch?.prices])

    const garagesWithPrice = useMemo(() => {
        const garagesBase = garagesWithAvailability ?? garages
        if (!garagesBase) return null
        if (!pricesByGarageId) return null

        return garagesBase.map(garage => {
            const currentGarageData = pricesByGarageId.get(garage.id)

            return currentGarageData ? {
                ...garage,
                amountCentsCzk: currentGarageData.amount_cents_czk,
                amountCentsHourlyCzk: currentGarageData.amount_cents_hourly_czk,
                amountCentsEur: currentGarageData.amount_cents_eur,
                amountCentsHourlyEur: currentGarageData.amount_cents_hourly_eur,
                reservationTimeRange: {
                    beginsAt: currentGarageData.begins_at,
                    endsAt: currentGarageData.ends_at,
                    minimumStayApplied: currentGarageData.minimum_stay_applied,
                },
                priceLoading: false,
            } : { ...garage, priceLoading }
        })
    }, [garagesWithAvailability, garages, pricesByGarageId, priceLoading])

    const priceRange = useMemo(
        () => getRange(
            garagesWithPrice,
            g => (currency === 'eur' ? g.amountCentsEur : g.amountCentsCzk),
            v => v / 100
        ),
        [garagesWithPrice, currency]
    )

    const heightRange = useMemo(
        () => getRange(garages, g => g.height),
        [garages]
    )

    const widthRange = useMemo(
        () => getRange(garages, g => g.width),
        [garages]
    )

    const lengthRange = useMemo(
        () => getRange(garages, g => g.length),
        [garages]
    )

    const weightRange = useMemo(
        () => normalizeWeightRange(getRange(garages, g => g.weight)),
        [garages]
    )
    
    const garageList = garagesWithPrice ?? garagesWithAvailability ?? garages ?? []
    const loadingGarageList = !garages
    const loadingGarageAvailability = availabilityQuery.isFetching && (availabilityQuery.isPending || availabilityQuery.isError)
    const availabilityErrors = [
        ...(availabilityQuery.isError
            ? getRequestErrors(availabilityQuery.error, t('minimumStayPrice.availabilityUnavailable'))
            : getResponseErrors(availabilityResponse)),
        ...(priceQuery.isError
            ? getRequestErrors(priceQuery.error, t('minimumStayPrice.unavailable'))
            : getResponseErrors(garagePricesBatch)),
    ]
    
    return {
        searchLocationPoint,
        garageList,
        loadingGarageList,
        loadingGarageAvailability,
        availabilityErrors,
        priceRange,
        heightRange,
        widthRange,
        lengthRange,
        weightRange,
    }
}

export const useGetLaterAvailability = (
    loadingGarageAvailability: boolean,
    garageList: Array<Garage>,
) => {

    const searchSubmission = useAtomValue(searchSubmissionAtom)
    const applySuggestedStartTime = useApplySuggestedStartTime({ urlVariant: 'map' })

    const {
        location,
        startDate,
        startTime,
        endDate,
        endTime,
    } = searchSubmission

    const { data: laterAvailabilityResponse } = useLaterAvailabilityQuery({
        enabled: !loadingGarageAvailability && garageList.some(garage => garage.timeRangeValid === true && !garage.available),
        includeAll: true,
    })
    const laterAvailabilityErrors = laterAvailabilityResponse?.errors.filter(
        error => typeof error === 'string',
    ) ?? []
    const laterAvailability = deriveLaterAvailability(
        garageList,
        laterAvailabilityResponse?.garages ?? [],
        startTime,
    )

    const searchKey = [
        location,
        startDate?.format('YYYY-MM-DD'),
        startTime,
        endDate?.format('YYYY-MM-DD'),
        endTime,
    ].join('|')
    const garagesAvailableLater: GarageAvailability | null = laterAvailability.garages.length > 0 && laterAvailability.options.length > 0 ? {
        garages: laterAvailability.garages,
        options: laterAvailability.options,
        searchedStartTime: startTime,
        searchKey,
        onSelectStartTime: applySuggestedStartTime,
    } : null

    return {
        garagesAvailableLater,
        laterAvailability,
        laterAvailabilityErrors,
    }
}
